import { useApolloClient } from '@apollo/react-hooks';
import RsvpSettings from '../../libs/component-rsvp-settings';
import { EventType } from '../../libs/component-rsvp-settings/EventTypes';
import { GuestWedding } from '../../libs/component-rsvp-settings/GuestWeddingTypes';
import { Household } from '../../libs/component-rsvp-settings/HouseholdTypes';
import { EventResponse, GuestWeddingResponse } from '../../libs/gds-api-client';
import produce from 'immer';
import React, { useMemo, useContext } from 'react';
import { useHistory } from 'react-router-dom';

import {
  EventsQuery,
  useEventsQuery,
  useHouseholdQuery,
  useWeddingWebsiteQuery,
  WeddingWebsiteQuery,
} from '../../generated/graphql';
import { TrackingContext } from '../../TrackingContext';
import removeEventTypename from '../../helpers/removeEventTypename';
import removeGuestWeddingTypename from '../../helpers/removeGuestWeddingTypename';
import { weddingWebsiteQuery } from '../Dashboard';
import FullScreenModal from '../Modals/FullScreenModal';
import { eventsQuery } from '../WeddingPageCard/graphql';
import { withTypename } from '../WeddingPageCard/helper';
import styles from './styles.scss';
import { guestWeddingWithTypename } from './helper';
import { useFlipper } from '../../hooks/useFlipper';

const EVENT_RR_EXP_CONTROL = 'control';
const EVENT_RR_EXP_VARIANT = 'variant';

const RsvpSettingsPage: React.FC<{}> = () => {
  const { track } = useContext(TrackingContext);
  const client = useApolloClient();
  const { data: gdsData } = useWeddingWebsiteQuery();
  const { data: eventsData } = useEventsQuery();
  const { data: householdData } = useHouseholdQuery();
  const history = useHistory();
  const events = useMemo(() => removeEventTypename(eventsData?.events), [eventsData]);
  const guestWedding = useMemo(() => removeGuestWeddingTypename(gdsData?.guestWedding), [gdsData]);

  const { variant } = useFlipper(
    process.env.REACT_APP_FLIPPER_EVENT_RAIL_VENUES_LINK_EXP_ID as string,
    EVENT_RR_EXP_CONTROL
  );
  const isExperiment = variant === EVENT_RR_EXP_VARIANT;

  const onEventsUpdate = (events: EventResponse[]) => {
    const cache = client.readQuery<EventsQuery>({ query: eventsQuery })!;

    const updated = produce(cache, draft => {
      events.forEach(event => {
        if (draft.events && draft.events.length) {
          const index = draft.events?.findIndex(e => e?.id === event.id);
          if (index >= 0) {
            draft.events[index] = withTypename(event);
          } else {
            draft.events.push(withTypename(event));
          }
        }
      });
    });

    client.writeQuery({ query: eventsQuery, data: { events: updated.events } });
  };

  const onSuccess = (data: GuestWeddingResponse) => {
    const { guestWedding, ...rest } = client.readQuery<WeddingWebsiteQuery>({ query: weddingWebsiteQuery })!;

    const updated = guestWeddingWithTypename(guestWedding, data);

    client.writeQuery({
      query: weddingWebsiteQuery,
      data: { guestWedding: updated, ...rest },
    });

    track('RSVP Settings Interaction', {
      action: `${updated?.allowGuestPreview ? 'enable' : 'disable'} public RSVP list`,
    });
  };

  return (
    <FullScreenModal className={styles.fullScreenModal}>
      {gdsData && householdData && eventsData ? (
        <RsvpSettings
          guestWedding={guestWedding as GuestWedding}
          events={events as EventType[]}
          households={householdData?.households as Household[]}
          isPrivateRsvp={Boolean(guestWedding?.isPrivateRsvp)}
          onSuccess={onSuccess}
          onEventsUpdate={onEventsUpdate}
          onCancelClick={history.goBack}
          goToGuestList={(eventId: string) => {
            window.location.assign(`/our-guest-list?event=${eventId}`);
          }}
          wwsState={gdsData?.weddingWebsite?.state || 'unpublished'}
          isExperiment={isExperiment}
        />
      ) : null}
    </FullScreenModal>
  );
};

export default RsvpSettingsPage;
